import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import {
  LoadingOverlay,
  Text,
  Button,
  Group as MantineGroup,
  Stack,
  Box,
  Alert,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconX } from "@tabler/icons-react";
import VerificationProcessingScreen from "./verification-processing-screen";
import DataCollectionIntro from "./data-collection-intro";
import FormField from "./form-field";
import { DataContext } from "../context/DataContext";
import useStatusCheck from "../hooks/useStatusCheck";
import useEdgeStatusCheck from "../hooks/useEdgeStatusCheck";
import useAdjustInputSize from "../hooks/useAdjustInputSize";
import useLocale from "../hooks/useLocale";
import { normalizeDob } from "../utils/validateDob";
import { getEdgeRetrieveUrl, getEdgeQueueUrl } from "../utils/edge";

// Maps the route's componentKey to the edge-mode queue name. Without
// this, every multi-step submission would land in the agematch queue
// regardless of which flow the user actually came from (COPPA, etc.).
const queueForComponent = (componentKey) => {
  if (componentKey === "idmatch/coppa") return "coppa";
  if (componentKey === "agematch/process") return "agematch";
  return "idmatchplus";
};

const AgematchForm = ({
  title,
  subtitle,
  icon,
  componentKey,
  endpoint,
  config,
  buttonText,
  reAttempts,
  onApiResponse,
  // Route step opt-in: when every field already validates from session
  // data, skip the intro and the steps and submit on mount. Off by
  // default: this form has never auto-submitted.
  autoSubmitPrefilled = false,
}) => {
  const { data, text, dispatch, settings } = useContext(DataContext);
  const theme = useMantineTheme();
  // Same locale namespace BasicDataCollectionForm uses for the
  // retry banner copy. Falls back to inline English if missing.
  const retryLocale = useLocale("coppa");
  const agematchLocale = useLocale("agematch");
  // Journey config can opt out of the "Verify your Age" intro screen
  // per-route — set `skipIntro: true` on the route in the journey
  // config (alongside `inputs`, `reattempts`, etc.) and the form
  // mounts straight to the fields. Also accepts a journey-level
  // `skipIntro` for back-compat / global default.
  const matchedRoute = settings?.journey?.route?.find(
    (route) => route.path === componentKey
  );
  const skipIntro =
    matchedRoute?.skipIntro === true ||
    settings?.journey?.settings?.skipIntro === true;
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(reAttempts);
  const [statusResponse, setStatusResponse] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasReceivedFailedResponse, setHasReceivedFailedResponse] = useState(false);
  const [showIntro, setShowIntro] = useState(!skipIntro);
  const [agematchStep, setAgematchStep] = useState(0); // 0: name, 1: address, 2: DOB
  const inputSize = useAdjustInputSize();
  const topBarRef = useRef(null);
  const [topBarHeight, setTopBarHeight] = useState(0);
  const bounceTransition = "cubic-bezier(0.68, -0.55, 0.27, 1.55)";
  const lastConfirmationRef = useRef(null);
  const confirmationsReceivedRef = useRef([]);
  const waitingForConfirmationChangeRef = useRef(false);
  const initialLoadRef = useRef(true);
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [hasShownFormForCurrentAttempt, setHasShownFormForCurrentAttempt] = useState(false);
  const [processingVerification, setProcessingVerification] = useState(false);
  // Terminal status captured for VerificationProcessingScreen. Set
  // by handleStatusResult on PASS or final-attempt FAIL; consumed by
  // the screen via resultReady. The screen owns the SET_STATUS +
  // SET_IS_CURRENT_COMPONENT_COMPLETE dispatch so the user always
  // sees "Completing verification…" before navigation.
  const [terminalStatus, setTerminalStatus] = useState(null);
  // Guards against re-triggering the IDMatchPlus questions fetch on every
  // 700ms status-poll tick while one is already in flight.
  const kbaQuestionsFetchingRef = useRef(false);

  // Edge mode detection
  const isEdgeMode = data?.edge === true;

  const buttonContentColor =
    data.styles?.components?.button?.color ||
    (theme.colorScheme === "dark" ? theme.white : theme.black);

  // Field grouping for agematch steps. A field counts as a "name"
  // field if it explicitly opts in via `group: "name"` OR if it's
  // one of the canonical fn/ln keys (COPPA uses these without the
  // group tag so the simple form stacks them instead of putting them
  // side-by-side).
  const isNameField = (field) =>
    field.group === "name" || field.name === "fn" || field.name === "ln";

  const getAgematchFieldGroups = () => {
    const nameFields = config.fields.filter(isNameField);
    const addressFields = config.fields.filter((field) => field.type === "address");

    console.log("AgeMatch field groups:", {
      nameFields,
      addressFields,
      allFields: config.fields,
      step: agematchStep
    });

    const dobFields = config.fields.filter(
      (field) =>
        field.type === "date" ||
        (!isNameField(field) && field.type !== "address"),
    );
    return { nameFields, addressFields, dobFields };
  };

  const { nameFields, addressFields, dobFields } = getAgematchFieldGroups();

  const getCurrentAgematchFields = () => {
    switch (agematchStep) {
      case 0: return nameFields;
      case 1: return addressFields;
      case 2: return dobFields;
      default: return nameFields;
    }
  };

  const getAgematchStepTitle = () => {
    switch (agematchStep) {
      case 0: return agematchLocale?.steps?.name || "Name details";
      case 1: return agematchLocale?.steps?.address || "Address details";
      case 2: return agematchLocale?.steps?.dob || "Date of birth and other details";
      default: return agematchLocale?.steps?.name || "Name details";
    }
  };

  const isAgematchStepValid = () => {
    const currentFields = getCurrentAgematchFields();
    return currentFields.every((field) => {
      if (field.type === "address") {
        const addressValue = {
          addr: form.values.addr || "",
          city: form.values.city || "",
          state: form.values.state || "",
          zip: form.values.zip || "",
        };
        const validationResult = field.validation ? field.validation(addressValue) : null;
        return validationResult === null;
      } else {
        const value = form.values[field.name] || "";
        const validationResult = field.validation ? field.validation(value) : null;
        return validationResult === null;
      }
    });
  };

  // Status check handler (same as basic form)
  const handleStatusResult = (statusResult, confirmationChanged) => {
    if (initialLoadRef.current && statusResult) {
      initialLoadRef.current = false;
    }

    setStatusResponse(statusResult);
    const confirmationNumber = statusResult.confirmation || "unknown";

    if (statusResult.process_state === "NEXT_APP_TRIGGER") {
      setTerminalStatus("NEXT_APP");
      setLoading(true);
      return false;
    }

    // Terminal first — the backend can return process_state="WAITING"
    // alongside is_final:true / attempts_remaining:0 when the final
    // attempt didn't match, so we have to detect "this is the end"
    // BEFORE we interpret WAITING as a retry signal. For PASS vs FAIL,
    // prefer the top-level `status`, but fall back to
    // `latest_event.status` because in some terminal responses the
    // top-level status stays "PENDING" and only the latest_event
    // carries the real PASS/FAIL.
    const isTerminal =
      statusResult.process_state === "COMPLETED" ||
      statusResult.is_final === true ||
      statusResult.attempts_remaining === 0;
    if (isTerminal) {
      const topStatus = statusResult.status;
      const eventStatus = statusResult.latest_event?.status;
      const finalStatus =
        topStatus === "PASS" || topStatus === "FAIL"
          ? topStatus
          : eventStatus === "PASS"
            ? "PASS"
            : "FAIL";
      setTerminalStatus(finalStatus);
      // Keep isVerifying / processingVerification as-is so the
      // VerificationProcessingScreen stays mounted and its onComplete
      // fires once `resultReady` flips — otherwise the form falls
      // through to a bare LoadingOverlay with no completion handler.
      setLoading(true);
      return false;
    }

    // IDMatchPlus KBA: "awaiting_user" means the challenge questions are
    // ready for the user to answer — this is expected mid-flow progress,
    // not a failed attempt. Go fetch the questions instead of falling into
    // the generic WAITING-as-retry branch below, which would burn the
    // (usually single) allowed attempt and bounce the user back to the
    // form with a misleading "Whoops" error.
    if (
      componentKey === "idmatch/plus" &&
      statusResult.transaction_state === "awaiting_user" &&
      !kbaQuestionsFetchingRef.current
    ) {
      kbaQuestionsFetchingRef.current = true;
      setProcessingVerification(false);
      pollForIdMatchPlusQuestions(settings.request_id);
      return false;
    }

    // Retry signal: process_state="WAITING" with attempts left.
    // Stop polling, hide the loading state, and surface the retry
    // banner so the user can correct + resubmit.
    if (statusResult.process_state === "WAITING") {
      setHasShownFormForCurrentAttempt(true);
      setIsVerifying(false);
      setLoading(false);
      setProcessingVerification(false);
      setResult(false);
      setHasReceivedFailedResponse(true);
      try {
        dispatch({ type: "SET_STATUS", payload: "PENDING" });
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
      } catch (err) {
        // Silent fail
      }
      return false;
    }

    if (statusResult.status === "PASS") {
      // Capture for the processing screen; navigation dispatched
      // from handleProcessingComplete once the screen plays through.
      setTerminalStatus("PASS");
      setLoading(true);
      return false;
    }

    if (
      statusResult.status === "FAIL" &&
      !hasShownFormForCurrentAttempt &&
      initialLoadRef.current
    ) {
      initialLoadRef.current = false;
      setCurrentAttempt(1);
      setHasShownFormForCurrentAttempt(true);
      setAttemptsLeft(reAttempts);
      setLoading(false);
      setProcessingVerification(false);
      setResult(false);
      return false;
    }

    if (waitingForConfirmationChangeRef.current && confirmationChanged) {
      waitingForConfirmationChangeRef.current = false;
      lastConfirmationRef.current = confirmationNumber;
      confirmationsReceivedRef.current.push(confirmationNumber);
      setHasShownFormForCurrentAttempt(false);

      if (statusResult.status !== "PASS" && attemptsLeft > 0) {
        setLoading(false);
        setProcessingVerification(false);
        setHasReceivedFailedResponse(true);
        setResult(false);
        return false;
      }

      if (statusResult.status !== "PASS" && attemptsLeft === 0) {
        // Capture for the processing screen; navigation dispatched
        // from handleProcessingComplete once the screen plays through.
        setTerminalStatus("FAIL");
        setLoading(true);
        return false;
      }

      setLoading(true);
      return true;
    }

    return true;
  };

  useEffect(() => {
    if (topBarRef.current) {
      setTopBarHeight(topBarRef.current.offsetHeight);
    }
  }, [showIntro]);

  // Reset to first step on reattempt. Note: we intentionally do NOT
  // clear `hasReceivedFailedResponse` here — the floating retry
  // banner needs to stay visible until the user dismisses it (or
  // resubmits, which clears it in submitHandler).
  useEffect(() => {
    if (attemptsLeft < reAttempts && hasReceivedFailedResponse) {
      setAgematchStep(0);
      kbaQuestionsFetchingRef.current = false;
      form.clearErrors();
    }
  }, [attemptsLeft, reAttempts, hasReceivedFailedResponse]);

  // Auto-dismiss the retry banner after 5 seconds. The countdown ring
  // in the banner UI mirrors this timer visually.
  useEffect(() => {
    if (!hasReceivedFailedResponse) return;
    const t = setTimeout(() => setHasReceivedFailedResponse(false), 5000);
    return () => clearTimeout(t);
  }, [hasReceivedFailedResponse]);

  const handleProcessingComplete = useCallback(() => {
    if (terminalStatus === "NEXT_APP") {
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      dispatch({ type: "TRIGGER_NAVIGATION" });
      return;
    }
    dispatch({ type: "SET_STATUS", payload: terminalStatus || "FAIL" });
    dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
  }, [terminalStatus, dispatch]);

  // Status check hooks - use edge status check when in edge mode
  useStatusCheck(!isEdgeMode && result, false, componentKey, handleStatusResult);
  useEdgeStatusCheck(isEdgeMode && result, handleStatusResult);

  // Form setup
  const form = useForm({
    initialValues: config.fields.reduce((acc, field) => {
      if (field.type === "address") {
        acc["addr"] = data.addr || "";
        acc["city"] = data.city || "";
        acc["state"] = data.state || "";
        acc["zip"] = data.zip || "";
      } else {
        acc[field.name] = data[field.name] || field.initialValue || "";
      }
      return acc;
    }, {}),
    validateInputOnChange: true,
    validateInputOnBlur: true,
    validate: (values) => {
      const errors = {};
      config.fields.forEach((field) => {
        if (field.validation) {
          let error;
          if (field.name === "address" && field.type === "address") {
            error = field.validation({
              addr: values.addr || "",
              city: values.city || "",
              state: values.state || "",
              zip: values.zip || "",
            });
          } else {
            error = field.validation(values[field.name]);
          }
          if (error) {
            if (field.type === "address") {
              errors.addr = error;
            } else {
              errors[field.name] = error;
            }
          }
        }
      });
      return errors;
    },
  });

  // Fetch IDMatchPlus challenge questions once the backend signals
  // "awaiting_user" — mirrors BasicDataCollectionForm's pollForQuestions.
  // onApiResponse hands the transformed payload up to idmatch/plus's page
  // component, which switches from this form to its own quiz UI.
  const pollForIdMatchPlusQuestions = async (requestId) => {
    const maxAttempts = 100; // ~5 minutes
    const pollInterval = 3000;
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(getEdgeRetrieveUrl(), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ request_id: requestId, type: "idmatchplus" }),
        });

        if (response.ok) {
          const result = await response.json();

          if (result.has_questions && result.questions && result.questions.length > 0) {
            const transformedResponse = {
              idr5_response: {
                output: { questions: { questions: result.questions } },
                continuations: { questions: { template: result.continuation_template } },
              },
            };
            if (typeof onApiResponse === "function") {
              onApiResponse(transformedResponse);
            }
            return; // Parent switches to the quiz UI; nothing left to poll here.
          }

          // No questions and a terminal outcome — hand off to the generic
          // status polling to resolve PASS/FAIL.
          if (!result.has_questions && (result.status === "PASS" || result.status === "FAIL" || result.action === "FAIL")) {
            kbaQuestionsFetchingRef.current = false;
            setIsVerifying(false);
            setLoading(true);
            setProcessingVerification(true);
            setResult(true);
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          console.error("IDMatchPlus questions polling timeout: no questions received after 5 minutes");
          kbaQuestionsFetchingRef.current = false;
          setIsVerifying(false);
          setLoading(false);
          setProcessingVerification(false);
        }
      } catch (error) {
        console.error("IDMatchPlus questions polling error:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          kbaQuestionsFetchingRef.current = false;
          setIsVerifying(false);
          setLoading(false);
          setProcessingVerification(false);
        }
      }
    };

    poll();
  };

  // Submit handler
  const submitHandler = async () => {
    setIsVerifying(true);
    // Clear any lingering retry banner — the user is making a new
    // attempt, so the previous "Whoops" should go away.
    setHasReceivedFailedResponse(false);
    const errors = form.validate();

    if (errors.hasErrors) {
      setIsVerifying(false);
      return;
    }

    const newAttemptsLeft = attemptsLeft - 1;
    setAttemptsLeft(newAttemptsLeft);

    Object.keys(form.values).forEach((key) => {
      data[key] = form.values[key];
    });

    if (!data.settings) data.settings = {};
    data.settings.currentAttempt = currentAttempt;
    data.settings.attemptsLeft = newAttemptsLeft;
    data.attemptsLeft = newAttemptsLeft;

    setLoading(true);

    try {
      let apiUrl;
      let requestData = { ...data };

      if (data?.edge === true) {
        const edgeQueueUrl = getEdgeQueueUrl();
        apiUrl = edgeQueueUrl;
        requestData.queue = queueForComponent(componentKey);
      } else {
        apiUrl = `${process.env.REACT_APP_BACKEND_URL}${endpoint}`;
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      })
        .then((res) => res.json())
        .then((responseData) => {
          // Edge mode: queue returns request_id, edge status polling handles the rest
          if (isEdgeMode) {
            // Store request_id for edge status polling if returned
            if (responseData.request_id) {
              dispatch({ type: "SET_SETTINGS", payload: { ...settings, request_id: responseData.request_id } });
            }
            // Start edge status polling
            setProcessingVerification(true);
            setIsVerifying(false);
            setResult(true);
            return;
          }

          if (typeof onApiResponse === "function") {
            onApiResponse(responseData);
          }

          try {
            dispatch({ type: "SET_STATUS", payload: "PENDING" });
            dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
          } catch (err) {
            // Silent fail
          }

          setTimeout(() => {
            setLoading(true);
            setProcessingVerification(true);
            setIsVerifying(false);
          }, 2000);

          setCurrentAttempt((prevAttempt) => prevAttempt + 1);
          setHasShownFormForCurrentAttempt(false);
          waitingForConfirmationChangeRef.current = true;
          setResult(true);
        });
    } catch (error) {
      alert("Something Went Wrong! Please try again.");
      setIsVerifying(false);
    }
  };

  const handleDobChange = (dob) => {
    const formattedDob = normalizeDob(`${dob.year || ""}${dob.month || ""}${dob.day || ""}`);
    form.setFieldValue("dob", formattedDob);
  };

  const handleAddressChange = (address) => {
    form.setFieldValue("addr", address.addr);
    form.setFieldValue("city", address.city);
    form.setFieldValue("state", address.state);
    form.setFieldValue("zip", address.zip);
  };

  // Let the on-screen keyboard's action key drive the flow so users never need
  // to reach the Continue button while typing. Enter moves to the next field on
  // the current step; on the last field it advances the step (or submits on the
  // final step) once the step is valid.
  const handleFormKeyDown = (e) => {
    if (e.key !== "Enter" || e.shiftKey || showIntro) {
      return;
    }

    // We fully control Enter here to avoid inconsistent native form submission.
    e.preventDefault();

    const focusables = Array.from(
      e.currentTarget.querySelectorAll("input:not([type=hidden]), select"),
    ).filter((el) => !el.disabled);

    const currentIndex = focusables.indexOf(e.target);
    if (currentIndex > -1 && currentIndex < focusables.length - 1) {
      focusables[currentIndex + 1].focus();
      return;
    }

    // On the last field of the step: advance or submit, but only when valid.
    if (!isAgematchStepValid()) {
      return;
    }

    if (agematchStep < 2) {
      setAgematchStep(agematchStep + 1);
    } else {
      submitHandler();
    }
  };

  // Initial load effect
  useEffect(() => {
    setCurrentAttempt(0);
    setHasShownFormForCurrentAttempt(false);
    setAttemptsLeft(reAttempts);
    setLoading(false);
    kbaQuestionsFetchingRef.current = false;
    form.clearErrors();
  }, [componentKey]);

  // Auto-submit once when the route step asks for it and the prefilled
  // values pass validation. Otherwise the intro / steps show as usual.
  const autoSubmittedRef = useRef(false);
  useEffect(() => {
    if (!autoSubmitPrefilled || autoSubmittedRef.current) return;
    const errors = form.validate();
    if (errors.hasErrors) {
      form.clearErrors();
      return;
    }
    autoSubmittedRef.current = true;
    setShowIntro(false);
    submitHandler();
  }, [autoSubmitPrefilled, componentKey]);

  // Show the processing screen the moment the user submits
  // (isVerifying), not only after the API responds. processingVerification
  // is now redundant for the screen — keep the LoadingOverlay fallback
  // for the brief loading-but-not-yet-processing window (e.g. quick
  // intermediate fetches that don't kick off a real verification).
  if (isVerifying || (loading && processingVerification)) {
    return (
      <VerificationProcessingScreen
        type="datamatch"
        resultReady={terminalStatus !== null}
        onComplete={handleProcessingComplete}
      />
    );
  }
  if (loading) {
    return <LoadingOverlay visible />;
  }

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes slideUpBounce {
            0% {
              opacity: 0;
              transform: translateY(20px);
            }
            60% {
              opacity: 1;
              transform: translateY(-5px);
            }
            80% {
              transform: translateY(3px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      {hasReceivedFailedResponse && (
        // Floating retry alert — positioned fixed so it hovers over
        // the form without pushing fields down. High z-index keeps
        // it above Mantine inputs. Top offset clears the iframe's
        // close-X header strip. Bounce-in entrance draws the eye so
        // the error registers immediately.
        <Box
          sx={{
            position: "fixed",
            top: 70,
            insetInlineStart: 16,
            insetInlineEnd: 16,
            zIndex: 1500,
            pointerEvents: "none",
            animation:
              "retryAlertBounceIn 550ms cubic-bezier(0.34, 1.56, 0.64, 1) both",
            "@keyframes retryAlertBounceIn": {
              "0%": { opacity: 0, transform: "translateY(-32px) scale(0.96)" },
              "60%": { opacity: 1, transform: "translateY(4px) scale(1.01)" },
              "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
            },
          }}
        >
          <Alert
            icon={<IconAlertCircle size={18} />}
            title={retryLocale?.retry?.title || "Whoops! That did not go as expected"}
            color="red"
            variant="light"
            styles={(t) => ({
              root: {
                pointerEvents: "auto",
                position: "relative",
                paddingInlineEnd: 48,
                backgroundColor: t.colors.red[0],
                borderColor: t.colors.red[2],
                boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
              },
              title: { color: t.colors.red[9], fontWeight: 600 },
              message: { color: t.colors.red[9] },
              icon: { color: t.colors.red[7] },
            })}
          >
            {retryLocale?.retry?.message ||
              "Please check that you've entered your full legal name, and provided an accurate date of birth or SSN."}
            {/* Countdown ring sits where the close-X would normally
                live. The stroke depletes over 5s in sync with the
                auto-dismiss useEffect. Clicking it dismisses early. */}
            <Box
              role="button"
              aria-label={retryLocale?.sharedMessages?.dismiss || "Dismiss"}
              onClick={() => setHasReceivedFailedResponse(false)}
              sx={{
                position: "absolute",
                top: 12,
                insetInlineEnd: 12,
                width: 28,
                height: 28,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "@keyframes retryRingDeplete": {
                  "0%": { strokeDashoffset: 0 },
                  "100%": { strokeDashoffset: 75.4 },
                },
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                style={{ position: "absolute", inset: 0 }}
              >
                <circle
                  cx="14"
                  cy="14"
                  r="12"
                  fill="none"
                  stroke={theme.colors.red[2]}
                  strokeWidth="2"
                />
                <circle
                  cx="14"
                  cy="14"
                  r="12"
                  fill="none"
                  stroke={theme.colors.red[7]}
                  strokeWidth="2"
                  strokeLinecap="round"
                  transform="rotate(-90 14 14)"
                  style={{
                    strokeDasharray: 75.4,
                    strokeDashoffset: 0,
                    animation: "retryRingDeplete 5s linear forwards",
                  }}
                />
              </svg>
              <IconX
                size={14}
                color={theme.colors.red[9]}
                style={{ position: "relative" }}
              />
            </Box>
          </Alert>
        </Box>
      )}
      <form
        onSubmit={form.onSubmit(submitHandler)}
        onKeyDown={handleFormKeyDown}
        style={{
          display: "flex",
          flexDirection: "column",
          animation: "fadeIn 0.6s ease-out",
          minHeight: "100%",
          maxWidth: "500px",
          margin: "0 auto",
          width: "100%",
        }}
      >
        {!showIntro && (
          <Box
            ref={topBarRef}
            sx={{
              position: "fixed",
              top: 0,
              insetInlineStart: 0,
              zIndex: 5,
              width: "100%",
              minHeight: 45,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor:
                theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
              borderBottom: `1px solid ${
                theme.colorScheme === "dark"
                  ? theme.colors.dark[5]
                  : theme.colors.gray[3]
              }`,
              padding: "8px 56px",
            }}
          >
            <Text
              fz="sm"
              fw={700}
              ta="center"
              c={theme.colorScheme === "dark" ? theme.colors.gray[2] : theme.black}
            >
              {getAgematchStepTitle()}
            </Text>
          </Box>
        )}

        <Box
          style={{
            marginTop: !showIntro ? topBarHeight : 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {showIntro && (
            <DataCollectionIntro
              icon={icon}
              title={title}
              subtitle={subtitle}
              componentKey={componentKey}
              inputSize={inputSize}
              data={data}
              onStart={() => setShowIntro(false)}
              bounceTransition={bounceTransition}
              theme={theme}
            />
          )}

          {!showIntro && (
            <Box
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Stack spacing="md" style={{ flexGrow: 1, width: "100%" }}>

                {/* Current step fields */}
                <div>
                  {/* Name fields (step 0) */}
                  {agematchStep === 0 && (
                    <div
                      style={{
                        width: "100%",
                        animation: `slideUpBounce 0.5s ${bounceTransition}`
                      }}
                    >
                      <Stack spacing="md" style={{ width: "100%" }}>
                        {nameFields.map((field) => (
                          <FormField
                            key={field.name}
                            field={field}
                            form={form}
                            settings={settings}
                            componentKey={componentKey}
                            showMoreFields={true}
                            onDobChange={handleDobChange}
                            onAddressChange={handleAddressChange}
                          />
                        ))}
                      </Stack>
                    </div>
                  )}

                  {/* Address fields (step 1) */}
                  {agematchStep === 1 && (
                    <div
                      style={{
                        width: "100%",
                        animation: `slideUpBounce 0.5s ${bounceTransition}`
                      }}
                    >
                      <Stack spacing="md" style={{ width: "100%" }}>
                        {addressFields.map((field) => (
                          <FormField
                            key={field.name}
                            field={field}
                            form={form}
                            settings={settings}
                            componentKey={componentKey}
                            showMoreFields={true}
                            onDobChange={handleDobChange}
                            onAddressChange={handleAddressChange}
                          />
                        ))}
                      </Stack>
                    </div>
                  )}

                  {/* DOB and other fields (step 2) */}
                  {agematchStep === 2 && (
                    <div
                      style={{
                        width: "100%",
                        animation: `slideUpBounce 0.5s ${bounceTransition}`
                      }}
                    >
                      <Stack spacing="md" style={{ width: "100%" }}>
                        {dobFields.map((field) => (
                          <FormField
                            key={field.name}
                            field={field}
                            form={form}
                            settings={settings}
                            componentKey={componentKey}
                            showMoreFields={true}
                            isSinglePageForm={true}
                            onDobChange={handleDobChange}
                            onAddressChange={handleAddressChange}
                          />
                        ))}
                      </Stack>
                    </div>
                  )}
                </div>

                {/* Back button for steps 1 and 2 */}
                {agematchStep > 0 && (
                  <div style={{ textAlign: "center", marginTop: "20px" }}>
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={() => setAgematchStep(agematchStep - 1)}
                      c={theme.colorScheme === "dark" ? theme.colors.gray[4] : theme.colors.gray[6]}
                      sx={{
                        fontSize: "13px",
                        height: "32px",
                        padding: "0 16px",
                        "&:hover": {
                          backgroundColor:
                            theme.colorScheme === "dark"
                              ? theme.colors.dark[5]
                              : theme.colors.gray[1],
                        },
                      }}
                    >
                      ← Back
                    </Button>
                  </div>
                )}
              </Stack>

              {/* Button positioned at bottom */}
              <Box style={{ marginTop: "15px", marginBottom: "20px", width: "100%" }}>
                <Button
                  fullWidth
                  size="lg"
                  type={agematchStep === 2 ? "submit" : "button"}
                  disabled={isVerifying || !isAgematchStepValid()}
                  loading={false}
                  onClick={(e) => {
                    if (agematchStep < 2) {
                      e.preventDefault();
                      setAgematchStep(agematchStep + 1);
                    }
                  }}
                  sx={{
                    transition: `all 0.5s ${bounceTransition}`,
                    "&:hover": { transform: "scale(1.01)" },
                    '&[data-loading="true"]': { color: buttonContentColor },
                  }}
                >
                  {isVerifying ? (
                    <MantineGroup spacing="xs" noWrap>
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid transparent",
                          borderTop: `2px solid ${theme.colors[theme.primaryColor]?.[6] ?? theme.colors.blue[6]}`,
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      <Text c="inherit">Sending Data</Text>
                    </MantineGroup>
                  ) : agematchStep === 2 ? (
                    buttonText
                  ) : (
                    agematchLocale?.button?.continue || "Continue"
                  )}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </form>
    </>
  );
};

export default AgematchForm;
